/**
 * Tenant Detail Page - Complete subscription management
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Skeleton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Alert,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Sms as SmsIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  useTenant,
  useModules,
  useActivateModule,
  useDeactivateModule,
  useUpdateTenantStatus,
  useCreateSubscription,
  useUpdateSubscription,
  useSmsUsage,
  usePriceCalculation,
  useUpdateModulePrice,
} from '../hooks';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { TenantFeatureFlagsSection } from '../components/tenant/TenantFeatureFlagsSection';
import { IndustryPacksSection } from '../components/tenant/IndustryPacksSection';
import { TenantAiSection } from '../components/tenant/TenantAiSection';
import { TenantUsageAnalyticsSection } from '../components/tenant/TenantUsageAnalyticsSection';
import type { TenantStatus, SubscriptionStatus, BillingCycle, CreateSubscriptionDto, UpdateSubscriptionDto } from '../types';

const statusColors: Record<TenantStatus, 'success' | 'warning' | 'error' | 'default'> = {
  PENDING: 'default',
  PROVISIONING: 'warning',
  ACTIVE: 'success',
  INACTIVE: 'error',
  FAILED: 'error',
};

const subscriptionColors: Record<SubscriptionStatus, 'success' | 'warning' | 'error' | 'default'> = {
  TRIAL: 'warning',
  ACTIVE: 'success',
  PAST_DUE: 'error',
  CANCELLED: 'error',
  SUSPENDED: 'error',
};

// Safe date formatting helper
const formatDate = (dateStr: string | null | undefined, formatStr: string = 'dd.MM.yyyy HH:mm'): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, formatStr);
  } catch {
    return 'Invalid date';
  }
};

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State for dialogs
  const [createSubDialogOpen, setCreateSubDialogOpen] = useState(false);
  const [editSubDialogOpen, setEditSubDialogOpen] = useState(false);
  const [editModulePriceDialogOpen, setEditModulePriceDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<{ key: string; name: string; basePrice: number; currentOverride: number | null } | null>(null);
  const [modulePriceOverride, setModulePriceOverride] = useState<string>('');

  // Create subscription form state
  const [createSubForm, setCreateSubForm] = useState<CreateSubscriptionDto>({
    status: 'TRIAL',
    moduleKeys: ['CORE'],
    smsIncluded: 200,
    isInternal: false,
  });

  // Edit subscription form state
  const [editSubForm, setEditSubForm] = useState<UpdateSubscriptionDto>({});

  // Data hooks
  const { data: tenant, isLoading } = useTenant(id!);
  const { data: allModules } = useModules();
  const { data: smsUsage } = useSmsUsage(id!);
  const { data: priceCalc } = usePriceCalculation(id!);

  // Mutation hooks
  const activateModule = useActivateModule();
  const deactivateModule = useDeactivateModule();
  const updateStatus = useUpdateTenantStatus();
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();
  const updateModulePrice = useUpdateModulePrice();

  const activeModuleKeys = tenant?.subscription?.modules
    ?.filter((m) => m.status === 'ACTIVE')
    ?.map((m) => m.module?.key ?? m.moduleKey) ?? [];

  const handleModuleToggle = async (moduleKey: string, isActive: boolean) => {
    if (!id) return;

    try {
      if (isActive) {
        await deactivateModule.mutateAsync({ tenantId: id, moduleKey });
        enqueueSnackbar(`Module ${moduleKey} deactivated`, { variant: 'success' });
      } else {
        await activateModule.mutateAsync({ tenantId: id, moduleKey });
        enqueueSnackbar(`Module ${moduleKey} activated`, { variant: 'success' });
      }
    } catch {
      enqueueSnackbar('Failed to update module', { variant: 'error' });
    }
  };

  const handleStatusChange = async (newStatus: TenantStatus) => {
    if (!id) return;

    try {
      await updateStatus.mutateAsync({ id, status: newStatus });
      enqueueSnackbar(`Tenant status updated to ${newStatus}`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update tenant status', { variant: 'error' });
    }
  };

  const handleSubscriptionStatusChange = async (newStatus: SubscriptionStatus) => {
    if (!id) return;

    try {
      await updateSubscription.mutateAsync({ tenantId: id, dto: { status: newStatus } });
      enqueueSnackbar(`Subscription status updated to ${newStatus}`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update subscription status', { variant: 'error' });
    }
  };

  const handleCreateSubscription = async () => {
    if (!id) return;

    try {
      await createSubscription.mutateAsync({ tenantId: id, dto: createSubForm });
      enqueueSnackbar('Subscription created successfully', { variant: 'success' });
      setCreateSubDialogOpen(false);
    } catch {
      enqueueSnackbar('Failed to create subscription', { variant: 'error' });
    }
  };

  const handleUpdateSubscription = async () => {
    if (!id) return;

    try {
      // Ensure all numeric fields are properly typed as numbers
      const dto = {
        ...editSubForm,
        smsIncluded: editSubForm.smsIncluded != null ? Number(editSubForm.smsIncluded) : undefined,
        smsOveragePrice: editSubForm.smsOveragePrice != null ? Number(editSubForm.smsOveragePrice) : undefined,
        discountPercent: editSubForm.discountPercent != null ? Number(editSubForm.discountPercent) : null,
        customPrice: editSubForm.customPrice != null ? Number(editSubForm.customPrice) : null,
      };

      await updateSubscription.mutateAsync({ tenantId: id, dto });
      enqueueSnackbar('Subscription updated successfully', { variant: 'success' });
      setEditSubDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update subscription';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const openEditDialog = () => {
    if (tenant?.subscription) {
      setEditSubForm({
        status: tenant.subscription.status,
        billingCycle: tenant.subscription.billingCycle,
        smsIncluded: tenant.subscription.smsIncluded,
        smsOveragePrice: tenant.subscription.smsOveragePrice,
        allowSmsOverage: tenant.subscription.allowSmsOverage,
        isInternal: tenant.subscription.isInternal,
        discountPercent: tenant.subscription.discountPercent,
        discountEndsAt: tenant.subscription.discountEndsAt,
        customPrice: tenant.subscription.customPrice,
        notes: tenant.subscription.notes,
      });
      setEditSubDialogOpen(true);
    }
  };

  const openModulePriceDialog = (moduleKey: string, moduleName: string, basePrice: number, currentOverride: number | null) => {
    setEditingModule({ key: moduleKey, name: moduleName, basePrice, currentOverride });
    setModulePriceOverride(currentOverride?.toString() ?? '');
    setEditModulePriceDialogOpen(true);
  };

  const handleUpdateModulePrice = async () => {
    if (!id || !editingModule) return;

    try {
      const priceOverride = modulePriceOverride === '' ? null : parseFloat(modulePriceOverride);
      await updateModulePrice.mutateAsync({
        tenantId: id,
        moduleKey: editingModule.key,
        dto: { priceOverride },
      });
      enqueueSnackbar(`Price updated for ${editingModule.name}`, { variant: 'success' });
      setEditModulePriceDialogOpen(false);
    } catch {
      enqueueSnackbar('Failed to update module price', { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={60} width={300} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Skeleton height={400} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton height={400} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box>
        <Alert severity="error">Tenant not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/tenants')} sx={{ mt: 2 }}>
          Back to Tenants
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/tenants')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={600}>
            {tenant.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tenant.slug}
          </Typography>
        </Box>
        {tenant.subscription?.isInternal && (
          <Chip label="Internal" color="info" />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Subscription & Modules */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Subscription & Modules
                </Typography>
                {tenant.subscription && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={openEditDialog}
                  >
                    Edit Subscription
                  </Button>
                )}
              </Box>

              {tenant.subscription ? (
                <>
                  {/* Subscription Status */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Status: ${tenant.subscription.status}`}
                      color={subscriptionColors[tenant.subscription.status]}
                    />
                    <Chip
                      label={`Billing: ${tenant.subscription.billingCycle}`}
                      variant="outlined"
                    />
                    {tenant.subscription.status === 'TRIAL' && tenant.subscription.trialEndsAt && (
                      <Chip
                        label={`Trial ends: ${formatDate(tenant.subscription.trialEndsAt, 'dd.MM.yyyy')}`}
                        variant="outlined"
                        color="warning"
                      />
                    )}
                    {tenant.subscription.discountPercent && tenant.subscription.discountPercent > 0 && (
                      <Chip
                        label={`${tenant.subscription.discountPercent}% rabatt`}
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Modules */}
                  <Typography variant="subtitle2" gutterBottom>
                    Moduler
                  </Typography>
                  <List disablePadding>
                    {allModules?.map((module) => {
                      const isModuleActive = activeModuleKeys.includes(module.key);
                      const isPending =
                        activateModule.isPending || deactivateModule.isPending;
                      const isDisabled = module.isCore || isPending || !module.isActive;

                      // Determine why toggle is disabled
                      const disabledReason = module.isCore
                        ? 'Grunnpakke - alltid inkludert'
                        : !module.isActive
                          ? 'Modul ikke tilgjengelig ennå'
                          : undefined;

                      return (
                        <ListItem key={module.key} divider sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {module.name}
                                {module.isCore && (
                                  <Chip label="Grunnpakke" size="small" color="primary" />
                                )}
                                {!module.isActive && (
                                  <Chip label="Kommer snart" size="small" />
                                )}
                              </Box>
                            }
                            secondary={module.description}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {module.isCore
                                  ? 'Inkludert'
                                  : module.basePrice > 0
                                    ? `${module.basePrice} kr/mnd`
                                    : 'Gratis'}
                              </Typography>
                              <Tooltip title={disabledReason || (isModuleActive ? 'Klikk for å deaktivere' : 'Klikk for å aktivere')}>
                                <span>
                                  <Switch
                                    checked={isModuleActive}
                                    onChange={() => handleModuleToggle(module.key, isModuleActive)}
                                    disabled={isDisabled}
                                  />
                                </span>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No subscription found for this tenant.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateSubDialogOpen(true)}
                  >
                    Create Subscription
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* SMS Usage */}
          {tenant.subscription && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SmsIcon color="primary" />
                  <Typography variant="h6">SMS Usage</Typography>
                </Box>

                {smsUsage ? (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {smsUsage.totalSent ?? 0} / {smsUsage.quota ?? 0} SMS used
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {smsUsage.remaining ?? 0} remaining
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={smsUsage.quota > 0 ? Math.min((smsUsage.totalSent / smsUsage.quota) * 100, 100) : 0}
                      color={(smsUsage.remaining ?? 0) < 20 ? 'error' : 'primary'}
                      sx={{ height: 10, borderRadius: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Chip label={`Sendt: ${smsUsage.totalSent ?? 0}`} size="small" color="success" variant="outlined" />
                      {(smsUsage.failed ?? 0) > 0 && (
                        <Chip label={`Feilet: ${smsUsage.failed}`} size="small" color="error" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ingen SMS-bruk data tilgjengelig
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Usage + quotas (ai-usage-billing) */}
          <TenantAiSection tenantId={id!} />

          {/* Usage analytics opt-out (activity-event-model D10) */}
          <TenantUsageAnalyticsSection tenantId={id!} />

          {/* Environments */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Environments
              </Typography>
              {tenant.environments && tenant.environments.length > 0 ? (
                <List disablePadding>
                  {tenant.environments.map((env) => (
                    <ListItem key={env.id} divider sx={{ px: 0 }}>
                      <ListItemText
                        primary={env.name}
                        secondary={`Created: ${formatDate(env.createdAt)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No additional environments
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Industry Packs */}
          <IndustryPacksSection
            tenantId={id!}
            tenantName={tenant.name}
            tenantSlug={tenant.slug}
          />

          {/* Feature Flags */}
          <TenantFeatureFlagsSection tenantId={id!} />
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Price Calculation */}
          {tenant.subscription && priceCalc && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h6">Monthly Price</Typography>
                </Box>

                {tenant.subscription.isInternal ? (
                  <Alert severity="info">Internal tenant - no billing</Alert>
                ) : (
                  <>
                    <List dense disablePadding>
                      {priceCalc.breakdown.map((m) => (
                        <ListItem
                          key={m.moduleKey}
                          sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                          onClick={() => openModulePriceDialog(m.moduleKey, m.moduleName, m.basePrice, m.priceOverride)}
                        >
                          <ListItemText
                            primary={m.moduleName}
                            secondary={m.priceOverride !== null ? `Standard: ${m.basePrice} kr` : undefined}
                          />
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={m.priceOverride !== null ? 600 : 400}>
                              {m.effectivePrice} kr
                            </Typography>
                            {m.priceOverride !== null && (
                              <Chip label="Custom" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Subtotal</Typography>
                      <Typography variant="body2">{priceCalc.basePrice} kr</Typography>
                    </Box>
                    {priceCalc.discountPercent && priceCalc.discountPercent > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', color: priceCalc.discountActive ? 'success.main' : 'text.secondary' }}>
                        <Box>
                          <Typography variant="body2">
                            Rabatt ({priceCalc.discountPercent}%)
                            {!priceCalc.discountActive && ' (utløpt)'}
                          </Typography>
                          {priceCalc.discountEndsAt && (
                            <Typography variant="caption" color="text.secondary">
                              {priceCalc.discountActive ? 'Utløper: ' : 'Utløp: '}
                              {formatDate(priceCalc.discountEndsAt, 'dd.MM.yyyy')}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ textDecoration: !priceCalc.discountActive ? 'line-through' : 'none' }}>
                          -{priceCalc.discount} kr
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight={600}>Total</Typography>
                      <Typography variant="subtitle1" fontWeight={600}>{priceCalc.finalPrice} kr/mnd</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Klikk på en modul for å endre pris
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tenant Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tenant Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={tenant.status}
                      color={statusColors[tenant.status]}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(tenant.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(tenant.updatedAt)}
                  </Typography>
                </Box>
                {tenant.subscription?.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {tenant.subscription.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tenant.status !== 'ACTIVE' && (
                  <Button
                    variant="outlined"
                    color="success"
                    fullWidth
                    onClick={() => handleStatusChange('ACTIVE')}
                    disabled={updateStatus.isPending}
                  >
                    Activate Tenant
                  </Button>
                )}
                {tenant.subscription && tenant.subscription.status !== 'SUSPENDED' && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => handleSubscriptionStatusChange('SUSPENDED')}
                    disabled={updateSubscription.isPending}
                  >
                    Suspend Subscription
                  </Button>
                )}
                {tenant.subscription && tenant.subscription.status === 'SUSPENDED' && (
                  <Button
                    variant="outlined"
                    color="success"
                    fullWidth
                    onClick={() => handleSubscriptionStatusChange('ACTIVE')}
                    disabled={updateSubscription.isPending}
                  >
                    Reactivate Subscription
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Subscription Dialog */}
      <Dialog open={createSubDialogOpen} onClose={() => setCreateSubDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createSubForm.status}
                label="Status"
                onChange={(e) => setCreateSubForm({ ...createSubForm, status: e.target.value as SubscriptionStatus })}
              >
                <MenuItem value="TRIAL">Trial</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="SMS Included"
              type="number"
              value={createSubForm.smsIncluded}
              onChange={(e) => setCreateSubForm({ ...createSubForm, smsIncluded: parseInt(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">per month</InputAdornment>,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={createSubForm.isInternal}
                  onChange={(e) => setCreateSubForm({ ...createSubForm, isInternal: e.target.checked })}
                />
              }
              label="Internal tenant (no billing)"
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Initial Modules</Typography>
            {allModules?.filter(m => m.isActive).map((module) => (
              <FormControlLabel
                key={module.key}
                control={
                  <Checkbox
                    checked={createSubForm.moduleKeys?.includes(module.key) || module.isCore}
                    onChange={(e) => {
                      if (module.isCore) return;
                      const newKeys = e.target.checked
                        ? [...(createSubForm.moduleKeys || []), module.key]
                        : (createSubForm.moduleKeys || []).filter((k) => k !== module.key);
                      setCreateSubForm({ ...createSubForm, moduleKeys: newKeys });
                    }}
                    disabled={module.isCore}
                  />
                }
                label={`${module.name} ${module.isCore ? '(Grunnpakke)' : module.basePrice > 0 ? `(${module.basePrice} kr/mnd)` : ''}`}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSubDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubscription}
            disabled={createSubscription.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editSubDialogOpen} onClose={() => setEditSubDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editSubForm.status || ''}
                label="Status"
                onChange={(e) => setEditSubForm({ ...editSubForm, status: e.target.value as SubscriptionStatus })}
              >
                <MenuItem value="TRIAL">Trial</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="PAST_DUE">Past Due</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Billing Cycle</InputLabel>
              <Select
                value={editSubForm.billingCycle || ''}
                label="Billing Cycle"
                onChange={(e) => setEditSubForm({ ...editSubForm, billingCycle: e.target.value as BillingCycle })}
              >
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </Select>
            </FormControl>

            <Divider />
            <Typography variant="subtitle2">SMS Settings</Typography>

            <TextField
              label="SMS Included"
              type="number"
              value={editSubForm.smsIncluded ?? ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, smsIncluded: parseInt(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">per month</InputAdornment>,
              }}
            />

            <TextField
              label="SMS Overage Price"
              type="number"
              value={editSubForm.smsOveragePrice != null ? editSubForm.smsOveragePrice : ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, smsOveragePrice: parseFloat(e.target.value) || 0 })}
              slotProps={{
                htmlInput: { step: '0.01', min: '0' },
                input: { endAdornment: <InputAdornment position="end">kr per SMS</InputAdornment> },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={editSubForm.allowSmsOverage ?? true}
                  onChange={(e) => setEditSubForm({ ...editSubForm, allowSmsOverage: e.target.checked })}
                />
              }
              label="Allow SMS overage"
            />

            <Divider />
            <Typography variant="subtitle2">Pricing</Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={editSubForm.isInternal ?? false}
                  onChange={(e) => setEditSubForm({ ...editSubForm, isInternal: e.target.checked })}
                />
              }
              label="Internal tenant (no billing)"
            />

            <TextField
              label="Discount"
              type="number"
              value={editSubForm.discountPercent ?? ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, discountPercent: parseInt(e.target.value) || null })}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="Leave empty for no discount"
            />

            <TextField
              label="Rabatt utløper"
              type="date"
              value={editSubForm.discountEndsAt ? editSubForm.discountEndsAt.split('T')[0] : ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, discountEndsAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
              InputLabelProps={{ shrink: true }}
              helperText="Tøm for permanent rabatt"
            />

            <TextField
              label="Custom Price (Override)"
              type="number"
              value={editSubForm.customPrice ?? ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, customPrice: parseFloat(e.target.value) || null })}
              InputProps={{
                endAdornment: <InputAdornment position="end">kr/mnd</InputAdornment>,
              }}
              helperText="Leave empty to use calculated price"
            />

            <Divider />

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={editSubForm.notes ?? ''}
              onChange={(e) => setEditSubForm({ ...editSubForm, notes: e.target.value || null })}
              placeholder="Internal notes about this subscription..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSubDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpdateSubscription}
            disabled={updateSubscription.isPending}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Module Price Dialog */}
      <Dialog open={editModulePriceDialogOpen} onClose={() => setEditModulePriceDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Endre pris for {editingModule?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Standardpris: {editingModule?.basePrice} kr/mnd
            </Alert>

            <TextField
              label="Egendefinert pris"
              type="number"
              value={modulePriceOverride}
              onChange={(e) => setModulePriceOverride(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">kr/mnd</InputAdornment>,
              }}
              helperText="Tøm feltet for å bruke standardpris"
              fullWidth
              autoFocus
            />

            {editingModule?.currentOverride !== null && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setModulePriceOverride('')}
              >
                Tilbakestill til standardpris
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModulePriceDialogOpen(false)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={handleUpdateModulePrice}
            disabled={updateModulePrice.isPending}
          >
            Lagre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
